import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

import OrderCard from "../../components/orders/OrderCard";
import Shipment from "../../components/Shipment";
import SmartDeliveryEmployeePage from "./SmartDeliveryEmployeePage";
import GeoFenceEmployeePage from "./GeoFenceEmployeePage";
import IoTDevicesEmployeePage from "./IoTDevicesEmployeePage";
import DemandForecast from "../../components/DemandForecast";
import SendSmsComponent from "../../components/SendSmsComponent";

import {
    PageHeader,
    Panel,
    KpiCard,
    DarkButton,
} from "../../components/ui/AdminUI";

export default function EmployeeDashboardPage() {
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("orders");

    useEffect(() => {
        if (tab !== "orders") return;

        setLoading(true);
        setError("");

        api.get("/orders/active")
            .then((res) => {
                setOrders(Array.isArray(res.data) ? res.data : []);
            })
            .catch((err) => {
                console.error(err);
                setError(
                    err?.response?.data?.message ||
                    "Failed to load active orders."
                );
            })
            .finally(() => setLoading(false));
    }, [tab]);

    const stats = useMemo(() => {
        return {
            activeOrders: orders.length,
            pendingOrders: orders.filter(
                (x) =>
                    String(x.status || x.shipment?.status || "")
                        .toLowerCase()
                        .includes("pending")
            ).length,
            confirmedOrders: orders.filter(
                (x) =>
                    String(x.status || x.shipment?.status || "")
                        .toLowerCase()
                        .includes("confirmed")
            ).length,
        };
    }, [orders]);

    const renderOrders = () => {
        if (loading) {
            return <p className="text-slate-500">Loading active orders...</p>;
        }

        if (error) {
            return (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            );
        }

        return orders.length === 0 ? (
            <p className="text-slate-500">No active orders.</p>
        ) : (
            <div className="grid gap-4">
                {orders.map((order) => (
                    <OrderCard key={order.id} order={order} role="employee" />
                ))}
            </div>
        );
    };

    const tabs = [
        ["orders", "Active Orders"],
        ["shipments", "Shipments"],
        ["smartdelivery", "Smart Delivery"],
        ["geofence", "GeoFence"],
        ["iot", "IoT Devices"],
        ["demandforecast", "Demand Forecast"],
        ["sms", "SMS Center"],
    ];

    const modules = [
        ["🚀", "Employee Control Center", "/employee/mission-control"],
        ["🌍", "Global Operations", "/employee/global-operations"],
        ["📈", "Analytics Center", "/employee/analytics-center"],
        ["🤖", "AI Command Center", "/employee/ai-command-center"],
        ["🚚", "Predictive Logistics", "/employee/predictive-logistics"],
        ["⭐", "Trust Engine v2", "/employee/intelligent-trust-engine"],
        ["📡", "IoT Control Center", "/employee/iot-control-center"],
        ["🧠", "Autonomous Decision", "/employee/autonomous-decision"],
        ["🛰", "Digital Twin", "/employee/digital-twin"],
    ];

    const renderContent = () => {
        switch (tab) {
            case "orders":
                return renderOrders();
            case "shipments":
                return <Shipment />;
            case "smartdelivery":
                return <SmartDeliveryEmployeePage />;
            case "geofence":
                return <GeoFenceEmployeePage />;
            case "iot":
                return <IoTDevicesEmployeePage />;
            case "demandforecast":
                return <DemandForecast />;
            case "sms":
                return <SendSmsComponent />;
            default:
                return renderOrders();
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Employee Control Center"
                subtitle="Manage active orders, shipments, IoT tracking, GeoFence monitoring and Smart Delivery operations."
                action={
                    <DarkButton onClick={() => navigate("/dashboard")}>
                        Personal Dashboard
                    </DarkButton>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <KpiCard
                    title="Active Orders"
                    value={stats.activeOrders}
                    color="blue"
                />

                <KpiCard
                    title="Pending"
                    value={stats.pendingOrders}
                    color="yellow"
                />

                <KpiCard
                    title="Confirmed"
                    value={stats.confirmedOrders}
                    color="green"
                />
            </div>

            <Panel
                title="Operations"
                subtitle="Switch between employee logistics work areas."
            >
                <div className="flex flex-wrap gap-3">
                    {tabs.map(([name, label]) => (
                        <button
                            key={name}
                            type="button"
                            onClick={() => setTab(name)}
                            className={`rounded-xl px-5 py-3 font-semibold transition ${tab === name
                                    ? "bg-blue-600 text-white shadow"
                                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </Panel>

            <Panel
                title="Advanced Employee Modules"
                subtitle="Open intelligent logistics, AI, IoT and digital twin modules."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {modules.map(([icon, title, path]) => (
                        <button
                            key={path}
                            type="button"
                            onClick={() => navigate(path)}
                            className="text-left rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:bg-white hover:shadow-lg transition"
                        >
                            <div className="text-4xl mb-3">{icon}</div>
                            <div className="text-lg font-black text-slate-900">
                                {title}
                            </div>
                            <div className="text-slate-500 text-sm mt-1">
                                Open module
                            </div>
                        </button>
                    ))}
                </div>
            </Panel>

            <Panel title="Workspace">
                {renderContent()}
            </Panel>
        </div>
    );
}